import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { CompanyName } from "@/services/backend/company/components/company-name";
import { TableCard } from "@/shared/components/tables/table-card";
import { Input } from "@/shared/components/ui/input";
import { FilterMode } from "@/shared/lib/filter/filter";
import { Spinner } from "@/shared/components/ui/spinner";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { FeatureAuthDeleteDialog } from "./feature-auth-delete-dialog";
import { FeatureAuthRule } from "../types/feature-auth-rule";
import { FeatureAuthEditDialog } from "./feature-auth-edit-dialog";
import {
  ListRequest,
  useAuthFeatureAuthListQuery,
} from "@/services/backend/auth/api/feature-auth";

export function FeatureAuthTable({
  request,
  onRequestChange,
}: {
  request: ListRequest;
  onRequestChange: (r: ListRequest) => void;
}) {
  const { data, isLoading, isFetching, error } =
    useAuthFeatureAuthListQuery(request);

  return (
    <div className="grid gap-2">
      <TableCard>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Feature")}</TableHead>
              <TableHead>{t("Rollen")}</TableHead>
              <TableHead>{t("Unternehmen")}</TableHead>
              <TableHead className="text-right">{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={4} />}
            {error && <TableRtkErrorRow error={error} colSpan={4} />}
            {data && data.items.length === 0 && <TableEmptyRow colSpan={4} />}
            {data &&
              data.items.map((rule) => (
                <FeatureAuthTableRow key={rule.feature} rule={rule} />
              ))}
          </TableBody>
        </Table>
      </TableCard>
      <div className="flex items-center">
        {isFetching && <Spinner className="ml-4" />}
        {data && (
          <PaginationLimitOffset
            offset={request.offset ?? 0}
            limit={request.limit ?? 0}
            totalCount={data.totalCount}
            setLimitOffset={(limit, offset) => {
              onRequestChange({ ...request, limit, offset });
            }}
            className="ml-auto"
          />
        )}
      </div>
    </div>
  );
}

function FeatureAuthTableRow({ rule }: { rule: FeatureAuthRule }) {
  return (
    <TableRow key={rule.feature}>
      <TableCell>{rule.feature}</TableCell>
      <TableCell>
        {!rule.requirement.roles || rule.requirement.roles.length === 0
          ? t("Keine Beschränkung")
          : rule.requirement.roles?.map((r) => r).join(", ")}
      </TableCell>
      <TableCell>
        {!rule.requirement.companies || rule.requirement.companies.length === 0
          ? t("Keine Beschränkung")
          : rule.requirement.companies?.map((cId) => (
              <CompanyName key={cId} companyId={cId} />
            ))}
      </TableCell>
      <TableCell className="flex justify-end gap-2">
        <FeatureAuthEditDialog rule={rule} />
        <FeatureAuthDeleteDialog feature={rule.feature} />
      </TableCell>
    </TableRow>
  );
}

export function FeatureAuthTableFilters({
  request,
  onRequestChange,
}: {
  request: ListRequest;
  onRequestChange: (r: ListRequest) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <Input
        value={request.feature?.values?.[0] ?? ""}
        onChange={(e) => {
          const { value } = e.target;
          onRequestChange({
            ...request,
            feature: {
              active: value.length > 0,
              mode: FilterMode.Like,
              values: value.length > 0 ? [value] : [],
            },
          });
        }}
        className="h-8"
        placeholder={t("Feature suchen")}
      />
    </div>
  );
}
